import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Rocket, Server, Palette, Users, MessageSquare, Shield, Video, MegaphoneIcon } from "lucide-react";

const services = [
  {
    icon: Rocket,
    title: "Performance Marketing",
    tag: "Growth Engine",
    color: "gold",
    items: ["Google Ads", "Meta Ads", "App Marketing", "Lead Generation", "Conversion Optimization", "Web Marketing"],
    desc: "ROI-driven campaigns that attract, engage, convert & scale — across every paid channel.",
  },
  {
    icon: Server,
    title: "Custom Software & AI",
    tag: "AI Integrated",
    color: "emerald",
    items: ["Custom CRM & ERP", "SaaS Platforms", "AI Chatbots", "Workflow Automation", "Mobile & Web Apps"],
    desc: "We develop intelligent business software tailored specifically to how you operate.",
  },
  {
    icon: Palette,
    title: "Branding & Web Development",
    tag: "Creative Studio",
    color: "gold",
    items: ["Brand Identity", "Premium Websites", "UI / UX Design", "E-commerce Stores", "Landing Pages"],
    desc: "Build memorable brands and premium websites designed to convert and command trust.",
  },
  {
    icon: Users,
    title: "Social Media Management",
    tag: "Social Growth",
    color: "emerald",
    items: ["Strategy & Planning", "Content Creation", "Page Management", "Community Engagement", "Performance Analysis"],
    desc: "Complete social presence with reels, design, influencer marketing & growth strategies.",
  },
  {
    icon: MessageSquare,
    title: "Communication Solutions",
    tag: "Reach Everywhere",
    color: "gold",
    items: ["Email Marketing", "Bulk SMS", "Outdoor Advertising", "PR", "Brand Promotion"],
    desc: "Direct, measurable and multi-channel outreach that keeps your brand top of mind.",
  },
  {
    icon: Shield,
    title: "Government Management",
    tag: "Silent Partner",
    color: "emerald",
    items: ["Campaign Management", "Welfare Scheme Promotion", "End-to-End Execution", "Confidential Operations"],
    desc: "We help Governments promote policies & welfare schemes — 100% private execution.",
  },
  {
    icon: Video,
    title: "Creative Solutions",
    tag: "Studio",
    color: "gold",
    items: ["Ad Creatives & Banners", "Video Production", "Motion Graphics", "Landing Page Design", "Brand Storytelling"],
    desc: "Ideas that stop the scroll — turned into performance-grade creative assets.",
  },
  {
    icon: MegaphoneIcon,
    title: "Affiliate Marketing",
    tag: "Publisher Network",
    color: "emerald",
    items: ["CPA · CPL · CPI · CPS · CPR", "Hybrid Campaigns", "Premium Publishers", "Native · Display · Video", "Programmatic Buying"],
    desc: "Access to 300+ premium publishers and performance partners across India & abroad.",
  },
];

export default function Services() {
  const [active, setActive] = useState(0);

  return (
    <section id="services" className="relative py-32 overflow-hidden">
      <div className="aurora bg-[#047857] w-[500px] h-[500px] top-40 -left-40" style={{opacity:0.15}} />
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div>
            <span className="eyebrow">Our Core Services</span>
            <h2 className="mt-6 text-4xl md:text-6xl font-semibold max-w-3xl leading-tight">
              End-to-end digital solutions to <span className="text-gold-gradient">accelerate your growth.</span>
            </h2>
          </div>
          <p className="text-white/60 max-w-md text-[15px] leading-relaxed">
            From branding and websites to software development, AI, digital marketing, paid
            advertising, media buying and automation — everything under one roof.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 space-y-2">
            {services.map((s, i) => (
              <button
                key={s.title}
                onClick={() => setActive(i)}
                className={`w-full text-left flex items-center gap-4 p-4 rounded-2xl border transition-all ${
                  active === i
                    ? "border-[#C9A227]/50 bg-[#C9A227]/5"
                    : "border-white/5 hover:border-white/15 bg-white/[0.015]"
                }`}
              >
                <div className={`w-11 h-11 rounded-xl grid place-items-center ${
                  s.color === "gold" ? "bg-[#C9A227]/10 border border-[#C9A227]/30" : "bg-[#10B981]/10 border border-[#10B981]/30"
                }`}>
                  <s.icon size={18} className={s.color === "gold" ? "text-[#F4D983]" : "text-[#6EE7B7]"} />
                </div>
                <div className="flex-1">
                  <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">{s.tag}</div>
                  <div className="text-base md:text-lg font-medium">{s.title}</div>
                </div>
                <div className={`text-xs ${active === i ? "text-[#F4D983]" : "text-white/30"}`}>0{i+1}</div>
              </button>
            ))}
          </div>

          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.45 }}
                className="glass rounded-3xl p-8 md:p-10 relative overflow-hidden min-h-[420px]"
              >
                <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#C9A227]/15 blur-3xl" />
                <div className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full bg-[#047857]/15 blur-3xl" />
                <div className="relative">
                  <div className="text-xs uppercase tracking-[0.28em] text-white/40">{services[active].tag}</div>
                  <h3 className="mt-3 text-3xl md:text-4xl font-semibold">
                    <span className={services[active].color === "gold" ? "text-gold-gradient" : "text-emerald-gradient"}>
                      {services[active].title}
                    </span>
                  </h3>
                  <p className="mt-4 text-white/70 leading-relaxed max-w-xl">{services[active].desc}</p>
                  <div className="mt-8 hairline" />
                  <div className="mt-8 grid sm:grid-cols-2 gap-4">
                    {services[active].items.map((it) => (
                      <div key={it} className="flex items-center gap-3 text-white/80">
                        <span className="divider-dot" />
                        {it}
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
